import {
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Title,
  TextInput,
} from '@patternfly/react-core'
import {
  format,
  isBefore,
  isToday,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type {
  IPatient} from '@app/domain/patient/patient.interface';
import {
  PatientBirthSexId,
} from '@app/domain/patient/patient.interface'
import { hasContent } from '@app/lib/string'
import { ValidationStatusId } from '@app/lib/validators'
import type * as Patients from '@app/state/patients'
import { invalidDateText } from '@app/state/patients/messages'
import { SmallButton } from '@app/utils/styles/small-button'

import { UpdatePatientStatus } from './patient-status'

interface Props {
  onCancelEdit: () => void
  onUpdatePatient?: (id: string, patient: Patients.IApiUpdatePatient) => void
  patient: IPatient
  updateStatus?: Patients.PatientActionStatusId
}

export const EditablePatientCard = ({
  onCancelEdit,
  onUpdatePatient,
  patient,
  updateStatus,
}: Props) => {
  const [birthSex, setBirthSex] = useState<string>(null)
  const [dateOfBirth, setDateOfBirth] = useState<string>(null)
  const [familyName, setFamilyName] = useState<string>(null)
  const [generalPractitioner, setGeneralPractitioner] = useState<string>(null)
  const [givenNames, setGivenNames] = useState<string>(null)
  const [medicalRecordNumber, setMedicalRecordNumber] = useState<string>(null)

  const [
    dateOfBirthValidationMessage,
    setDateOfBirthValidationMessage,
  ] = useState('')

  useEffect(() => {
    setBirthSex(patient.birthSex)
    setDateOfBirth(patient.dateOfBirth)
    setFamilyName(patient.familyName)
    setGeneralPractitioner(patient.generalPractitioner)
    setGivenNames(patient.givenNames)
    setMedicalRecordNumber(patient.medicalRecordNumber)
  }, [patient])

  const validateIfFieldHasContent = useCallback((field) => {
    return hasContent(field)
      ? ValidationStatusId.Success
      : ValidationStatusId.Error
  }, [])

  const isDateOfBirthValid = useCallback((value: string): boolean => {
    if (!value) {
      setDateOfBirthValidationMessage('Date of Birth is required')
      return false
    }

    const parsedDate = parseISO(value)

    if (!isValid(parsedDate)) {
      setDateOfBirthValidationMessage(invalidDateText)
      return false
    }

    if (isBefore(parsedDate, startOfDay(new Date())) || isToday(parsedDate)) {
      setDateOfBirthValidationMessage('')
      return true
    } else {
      setDateOfBirthValidationMessage('Date must be in the past.')
      return false
    }
  }, [])

  const {
    dateOfBirthValidation,
    familyNameValidation,
    givenNameValidation,
    medicalRecordNumberValidation,
  } = useMemo(() => {
    return {
      dateOfBirthValidation: isDateOfBirthValid(dateOfBirth)
        ? ValidationStatusId.Success
        : ValidationStatusId.Error,
      familyNameValidation: validateIfFieldHasContent(familyName),
      givenNameValidation: validateIfFieldHasContent(givenNames),
      medicalRecordNumberValidation: validateIfFieldHasContent(
        medicalRecordNumber,
      ),
    }
  }, [
    dateOfBirth,
    familyName,
    givenNames,
    isDateOfBirthValid,
    medicalRecordNumber,
    validateIfFieldHasContent,
  ])

  const isFormValid = useMemo(() => {
    return (
      hasContent(medicalRecordNumber) &&
      hasContent(familyName) &&
      hasContent(givenNames) &&
      hasContent(dateOfBirth) &&
      isDateOfBirthValid(dateOfBirth)
    )
  }, [
    dateOfBirth,
    familyName,
    givenNames,
    isDateOfBirthValid,
    medicalRecordNumber,
  ])

  const handleUpdatePatient = useCallback(() => {
    if (isFormValid) {
      const updatedPatient = {
        ...patient,
        birthSex,
        dateOfBirth,
        familyName,
        generalPractitioner,
        givenNames,
        medicalRecordNumber,
      }
      onUpdatePatient(patient.id, updatedPatient)
    }
  }, [
    birthSex,
    dateOfBirth,
    familyName,
    generalPractitioner,
    givenNames,
    isFormValid,
    medicalRecordNumber,
    onUpdatePatient,
    patient,
  ])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      handleUpdatePatient()
    },
    [handleUpdatePatient],
  )

  return (
    <Flex direction={{ default: 'column' }} flex={{ default: 'flex_1' }}>
      <FlexItem>
        <Flex direction={{ default: 'row' }}>
          <FlexItem>
            <Title headingLevel="h2" size="lg">
              Patient Information
            </Title>
          </FlexItem>
          <FlexItem>
            <SmallButton variant="secondary" onClick={onCancelEdit}>
              Cancel
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <SmallButton variant="primary" onClick={handleUpdatePatient}>
              Save
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <UpdatePatientStatus updateStatus={updateStatus} />
          </FlexItem>
        </Flex>
      </FlexItem>

      <Form isHorizontal onSubmit={handleSubmit}>
        <FormGroup
          label="Family Name"
          isRequired
          fieldId="family-name"
          validated={familyNameValidation}
          helperTextInvalid="Family name is required"
        >
          <TextInput
            value={familyName}
            isRequired
            type="text"
            id="family-name"
            name="familyName"
            onChange={setFamilyName}
          />
        </FormGroup>
        <FormGroup
          label="Given Name(s)"
          isRequired
          fieldId="given-names"
          validated={givenNameValidation}
          helperTextInvalid="Given name is required"
        >
          <TextInput
            value={givenNames}
            isRequired
            type="text"
            id="given-names"
            name="givenNames"
            onChange={setGivenNames}
          />
        </FormGroup>
        <FormGroup
          label="Medical Record Number"
          isRequired
          fieldId="medical-record-number"
          validated={medicalRecordNumberValidation}
          helperTextInvalid="MRN is required"
        >
          <TextInput
            value={medicalRecordNumber}
            isRequired
            type="text"
            id="medical-record-number"
            name="medicalRecordNumber"
            onChange={setMedicalRecordNumber}
          />
        </FormGroup>
        <FormGroup
          label="Date of Birth"
          isRequired
          fieldId="date-of-birth"
          validated={dateOfBirthValidation}
          helperTextInvalid={dateOfBirthValidationMessage}
        >
          <TextInput
            isRequired
            type="date"
            onChange={setDateOfBirth}
            value={dateOfBirth ?? ''}
            max={format(new Date(), 'yyyy-MM-dd')}
          />
        </FormGroup>
        <FormGroup isRequired fieldId="birth-sex" label="Birth Sex">
          <FormSelect
            id="birth-sex"
            value={birthSex}
            isRequired
            onChange={setBirthSex}
          >
            {Object.keys(PatientBirthSexId).map((key, idx) => (
              <FormSelectOption
                key={idx}
                value={PatientBirthSexId[key]}
                label={key}
              />
            ))}
          </FormSelect>
        </FormGroup>
        <FormGroup label="Family Doctor" fieldId="general-practitioner">
          <TextInput
            value={generalPractitioner}
            type="text"
            id="general-practitioner"
            name="generalPractitioner"
            onChange={setGeneralPractitioner}
          />
        </FormGroup>
      </Form>
    </Flex>
  )
}
